import { useId, type ReactElement, type ReactNode } from "react";
import { IconButton } from "./Button.tsx";
import { classes } from "./classes.ts";
import { KIT_METRICS, logical } from "./metrics.ts";
import { sizeLabel } from "./place.ts";
import { useScrollCue } from "./scroll-cue.ts";
import { RESIZE_EDGES, type WindowFrame } from "./window-frame.ts";

/**
 * Frames (brief section 5.1): Window, Panel, HudFrame and Divider. Each frame is its atlas frame drawn by
 * the generated `border-image` rule, stretched, never tiled. A window catches the pointer over its whole
 * box, since a click on a menu must not walk the character; a HUD frame sits over the world and takes no
 * pointer events, so a click on it or between two of them reaches the world, and only the controls inside
 * it take the pointer.
 */

/**
 * A size in logical pixels as a CSS length, or nothing when it is not given.
 *
 * @param n Logical pixels, or undefined.
 * @returns The length, or undefined.
 */
function optional(n: number | undefined): string | undefined {
  return n === undefined ? undefined : logical(n);
}

/**
 * The grip's side in logical pixels: a 10 square control, the chat grip's drawn again.
 */
const GRIP_SIZE = 10;

/**
 * A grip dot's side in logical pixels.
 */
const GRIP_DOT = 2;

/**
 * The grip's six dots by their left and top in the grip, the chat grip's triangle turned into the bottom right corner.
 */
const GRIP_DOTS: readonly (readonly [number, number])[] = [
  [7, 7],
  [4, 7],
  [1, 7],
  [7, 4],
  [4, 4],
  [7, 1],
];

/**
 * The grip of a framed window: a 10 square control in the bottom right corner with the chat grip's six dots, which
 * resizes by the pointer from its corner and by the arrow keys, and is described by the size label while that shows.
 * `Window` and the float draw it, each with its own class for where it stands.
 *
 * @param props The `frame` it belongs to, the `sizeId` of the size label it is described by while the window is sized,
 *   and the `className` that places it.
 * @returns The grip.
 */
export function FrameGrip({ frame, sizeId, className }: { readonly frame: WindowFrame; readonly sizeId: string; readonly className: string }): ReactElement {
  return (
    <button
      type="button"
      className={className}
      data-kit="window-grip"
      data-edge="bottomRight"
      aria-label={frame.grip.label}
      aria-describedby={frame.sizing ? sizeId : undefined}
      style={{ width: logical(GRIP_SIZE), height: logical(GRIP_SIZE) }}
      {...frame.edges}
      onKeyDown={frame.grip.onKeyDown}
      onFocus={frame.grip.onFocus}
      onBlur={frame.grip.onBlur}
    >
      {GRIP_DOTS.map(([left, top]) => (
        <span key={`${left}-${top}`} className="ot-window__grip-dot" aria-hidden="true" style={{ left: logical(left), top: logical(top), width: logical(GRIP_DOT), height: logical(GRIP_DOT) }} />
      ))}
    </button>
  );
}

/**
 * A window: the window frame, a title bar with its title in the pixel font and, when it can be closed, a
 * close button, and a body. A window that cannot be closed has a title bar of its title's row alone; one that
 * can is as tall as its close button (ADR-061's amendment "the kit and the entry forms match the design").
 *
 * A window may carry a footer, as the design draws it (ADR-061's amendment "character select as redesigned"): a row
 * under the body inside the frame, its buttons at the right and `KIT_METRICS.window.footer` apart and around.
 *
 * A window may be framed (ADR-061's amendment "every game window moves and resizes"): given `frame`, its title bar is
 * the handle of a drag, wearing the move cursor, and its title is a tab stop that takes the arrow keys and Home, shows
 * the focus ring and is described by the frame's hint; its four edges and corners, each with its resize cursor, are
 * handles of a resize; and its bottom right corner draws the grip, the chat grip's six dots, the window's last tab
 * stop. While it is dragged or the keyboard has it, the window wears a ring in the focus colour and shows its size in
 * a `status` region. Only the kit's `PlayWindow` and `ModalWindow` make a frame, by the id their page gives them; a
 * window given none has no handle of any kind.
 *
 * A window may carry `actions`, small controls of its own in the title bar to the left of Close (the Map's Float, P2.12-T23):
 * the page makes them, with the same press rules as Close, so a press on one is its own and never starts a move.
 *
 * @param props The `title`, the body, `onClose` when the window can be closed (its close button is the
 *   pointer and touch path; the page decides which key, if any, also closes it), `actions` for the title bar's other
 *   controls, a `width` and `height`
 *   in logical pixels, the `footer`'s buttons when it has one, `frame` when the window is framed, and the body's
 *   `padding` in logical pixels, the kit's window padding unless given (the Inventory window's is the design's 5).
 * @returns The window, a region named by its title.
 */
export function Window({ title, children, onClose, actions, width, height, footer, frame, padding = KIT_METRICS.window.padding }: { readonly title: string; readonly children: ReactNode; readonly onClose?: () => void; readonly actions?: ReactNode; readonly width?: number; readonly height?: number; readonly footer?: ReactNode; readonly frame?: WindowFrame | null | undefined; readonly padding?: number | undefined }): ReactElement {
  const titleId = useId();
  const hintId = useId();
  const sizeId = useId();
  const cue = useScrollCue();
  const move = frame?.move;
  const metrics = KIT_METRICS.window;
  const closes = onClose !== undefined;
  return (
    <section className={classes("ot-window ot-ui-frame-window", frame !== undefined && frame !== null && "ot-window--framed", frame?.sizing === true && "ot-window--sizing")} aria-labelledby={titleId} data-kit="window" style={{ width: optional(width), height: optional(height) }}>
      <header
        className={classes("ot-window__header", move !== undefined && "ot-window__header--move")}
        data-kit="window-header"
        onMouseDown={move?.onMouseDown}
        onPointerDown={move?.onPointerDown}
        onPointerMove={move?.onPointerMove}
        onPointerUp={move?.onPointerUp}
        onPointerCancel={move?.onPointerCancel}
        onLostPointerCapture={move?.onLostPointerCapture}
        onDoubleClick={move?.onDoubleClick}
        style={{ height: logical(closes ? metrics.header : metrics.titleRow), paddingLeft: logical(metrics.headerPaddingLeft) }}
      >
        <h2 id={titleId} className="ot-window__title" tabIndex={move === undefined ? undefined : 0} aria-describedby={move === undefined ? undefined : hintId} onKeyDown={move?.onKeyDown} style={{ marginTop: logical(closes ? metrics.titleOffset : metrics.titleRowOffset) }}>
          {title}
        </h2>
        {move === undefined ? null : (
          <span id={hintId} hidden>
            {move.hint}
          </span>
        )}
        {actions === undefined ? (
          closes ? (
            <IconButton icon="close" label="Close" onClick={onClose} />
          ) : null
        ) : (
          <span className="ot-window__tools" data-kit="window-tools">
            {actions}
            {closes ? <IconButton icon="close" label="Close" onClick={onClose} /> : null}
          </span>
        )}
      </header>
      <div ref={cue} className="ot-window__body ot-scroll" data-kit="window-body" style={{ padding: logical(padding) }}>
        {children}
      </div>
      {footer === undefined ? null : (
        <footer className="ot-window__footer" data-kit="window-footer" style={{ gap: logical(metrics.footer.gap), paddingTop: logical(metrics.footer.top), paddingLeft: logical(metrics.footer.side), paddingRight: logical(metrics.footer.side), paddingBottom: logical(metrics.footer.bottom) }}>
          {footer}
        </footer>
      )}
      {frame === undefined || frame === null ? null : (
        <>
          {RESIZE_EDGES.map((edge) => (
            <div key={edge} className={`ot-window__edge ot-window__edge--${edge}`} data-kit="window-edge" data-edge={edge} aria-hidden="true" {...frame.edges} />
          ))}
          <FrameGrip frame={frame} sizeId={sizeId} className="ot-window__grip" />
          {frame.sizing ? (
            <span className="ot-window__size" id={sizeId} role="status" data-kit="window-size">
              {sizeLabel(frame.size)}
            </span>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * An inset panel: a sunken area inside a window.
 *
 * @param props The content; a `width` and `height` in logical pixels; and the `padding` inside the frame's insets,
 *   the kit's panel padding unless given (a list sits straight inside its frame's insets, as the design's does).
 * @returns The panel.
 */
export function Panel({ children, width, height, padding = KIT_METRICS.panel.padding }: { readonly children: ReactNode; readonly width?: number; readonly height?: number; readonly padding?: number }): ReactElement {
  return (
    <div className="ot-panel ot-ui-frame-panel" data-kit="panel" style={{ padding: logical(padding), width: optional(width), height: optional(height) }}>
      {children}
    </div>
  );
}

/**
 * A HUD frame over the world. It takes no pointer events; controls inside it take their own.
 *
 * @param props The content, a `width` and `height` in logical pixels, and the `padding` inside the frame's edge in
 *   logical pixels, the kit's HUD padding unless given (the chat panel keeps the design's 1).
 * @returns The frame.
 */
export function HudFrame({ children, width, height, padding = KIT_METRICS.hud.padding }: { readonly children: ReactNode; readonly width?: number; readonly height?: number; readonly padding?: number }): ReactElement {
  return (
    <div className="ot-hud ot-ui-frame-hud" data-kit="hud-frame" style={{ padding: logical(padding), width: optional(width), height: optional(height) }}>
      {children}
    </div>
  );
}

/**
 * A divider two logical pixels thick: a dark line over a light one, across or down.
 *
 * @param props `vertical` for a divider between side-by-side content, and the `margin` it keeps on each side in
 *   logical pixels, the kit's divider margin unless given (a menu's divider keeps a smaller one).
 * @returns The separator.
 */
export function Divider({ vertical = false, margin: space = KIT_METRICS.divider.margin }: { readonly vertical?: boolean; readonly margin?: number }): ReactElement {
  const thickness = logical(KIT_METRICS.divider.thickness);
  const margin = logical(space);
  return (
    <div
      role="separator"
      aria-orientation={vertical ? "vertical" : "horizontal"}
      className={vertical ? "ot-divider ot-divider--vertical" : "ot-divider"}
      data-kit="divider"
      style={vertical ? { width: thickness, marginLeft: margin, marginRight: margin } : { height: thickness, marginTop: margin, marginBottom: margin }}
    />
  );
}
